import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useUnit } from 'effector-react';
import { $profileFeatureStore } from '@lib/store/profile';
import { useCardFlow, useCurrencySymbol } from '@shared/hooks';
import { type CardDetailsResponse, type CardLimitsResponse, CardStatus, Frequency } from '@shared/api/rest/cards';
import { FormatUtils } from '@shared/utils';
import { SidebarItem, SidebarRow } from '@widgets';
import { LimitRow } from '@widgets/card/limitRow';
import { Button, Icon, Text } from '@shared/widgets';
import SettingIcon from '@assets/icons/setting.svg';
import SwapIcon from '@assets/icons/swap.svg';
import CancelIcon from '@assets/icons/cancel.svg';
import NotificationIcon from '@assets/icons/notification.svg';

interface ICardSidebarItemsProps {
  card: CardDetailsResponse;
  limits?: CardLimitsResponse;
  openBlockModal: (arg: boolean) => void;
  openPinModal: () => void;
  openReportModal: () => void;
  openLimitsModal: () => void;
  openEditRecipientsModal: () => void;
  openDetailsModal: () => void;
  showBlockModal: boolean;
}

export const CardSidebarItems = (props: ICardSidebarItemsProps) => {
  const { id } = useParams();

  const profileFeatureStore = useUnit($profileFeatureStore.store);
  const cardFlow = useCardFlow(profileFeatureStore.data?.cardFlows, id ? parseInt(id) : undefined);

  const blocked = useMemo(() => props.card.status === CardStatus.FROZEN || props.card.status === CardStatus.BLOCKED, [props.card]);

  useEffect(() => {
    if (props.showBlockModal) {
      props.openBlockModal(false);
    }
  }, [props.card]);

  useEffect(() => {
    props.openBlockModal(false);
  }, [blocked]);

  const currentLimitValues = useMemo(() => {
    if (!props.limits) return null;
    if (props.limits.frequency === Frequency.DAILY) return props.limits.dayLimits;
    else if (props.limits.frequency === Frequency.CYCLE) return props.limits.cycleLimits;
    else if (props.limits.frequency === Frequency.YEAR) return props.limits.yearLimits;
    else return null;
  }, [props.limits]);

  const symbol = useCurrencySymbol(props.card.currency);

  return (
    <div className='p-6 bg-white dark:bg-soft-black w-80'>
      <SidebarItem title='Card details' icon={<Icon icon={<SettingIcon />} color='inherit' />}>
        <SidebarRow leftRowText='Card label' rightRowText={props.card.cardLabel || 'None'} />
        <SidebarRow leftRowText='Card number' rightRowText={props.card.cardNumber && FormatUtils.toSliceNumber(props.card.cardNumber)} />
        <SidebarRow leftRowText='Card category' rightRowText={props.card.category || 'None'} />
        {cardFlow?.editCardLabel && (
          <Button color='outlined' className='mb-5' fullWidth onClick={props.openDetailsModal}>
            Edit details
          </Button>
        )}
      </SidebarItem>
      <SidebarItem title='Card actions' icon={<Icon icon={<SwapIcon />} color='inherit' />}>
        {cardFlow?.sendPinReminder && (
          <Button color='outlined' className='mb-5' fullWidth onClick={props.openPinModal}>
            Request PIN
          </Button>
        )}
        {cardFlow?.block && (
          <Button color='outlined' className='mb-5' fullWidth onClick={() => props.openBlockModal(true)}>
            {(blocked ? 'Unfreeze' : 'Freeze') + ' card'}
          </Button>
        )}
        {cardFlow?.reportCardLostStolen && (
          <Button color='outlined' className='mb-5' fullWidth onClick={props.openReportModal}>
            Report lost/stolen card
          </Button>
        )}
      </SidebarItem>
      {props.limits && currentLimitValues && (
        <SidebarItem title='Limits' icon={<Icon icon={<CancelIcon />} color='inherit' />}>
          <SidebarRow leftRowText='Frequency' rightRowText={props.limits.frequency} />
          <LimitRow
            label='Retail limit'
            currentAmount={currentLimitValues.retailAmount}
            limitAmount={props.limits.currentRetailAmount}
            limitNumber={props.limits.currentRetailNumber}
            currentNumber={currentLimitValues.retailNumber}
            symbol={symbol}
          />
          <LimitRow
            label='Cash limit'
            limitAmount={props.limits.currentCashAmount}
            currentAmount={currentLimitValues.cashAmount}
            limitNumber={props.limits.currentCashNumber}
            currentNumber={currentLimitValues.cashNumber}
            symbol={symbol}
          />
          <LimitRow
            label='Total limit'
            limitAmount={props.limits.currentOTCAmount}
            currentAmount={currentLimitValues.otcAmount}
            limitNumber={props.limits.currentOTCNumber}
            currentNumber={currentLimitValues.otcNumber}
            symbol={symbol}
          />
          {cardFlow?.updateCardLimit && (
            <Button color='outlined' className='mb-5' fullWidth onClick={props.openLimitsModal}>
              Request new limits
            </Button>
          )}
        </SidebarItem>
      )}
      <SidebarItem
        title='Alerts & notifications'
        icon={<Icon icon={<NotificationIcon />} color='inherit' />}
        isHidden={!cardFlow?.cardAlertsSettings.declinedAuths}
      >
        <Text className='mb-5' size='sm'>
          Declined Authorisations
        </Text>
        {props.card.declinedAuthorizationSubscription?.recipientDetails.map((item, idx) => (
          <SidebarRow key={idx} leftRowText={item.firstName || ''} rightRowText={item.email} />
        ))}
        <Button color='outlined' className='mb-5' fullWidth onClick={props.openEditRecipientsModal}>
          Edit recipients
        </Button>
      </SidebarItem>
    </div>
  );
};
